import { getPokemonMasterList, loadPokemonMasterList } from "../lib/data-access";
import { getPokemonMovesByUsage } from "../lib/move-access";
import { getAbilitiesByUsage } from "../lib/ability-access";
import type { PokemonV2 } from "../types/pokemon";

export type {
  Pokemon,
  PokemonV2,
  PokemonOffenceInterface,
  PokemonDefenceInterface,
  PokemonStatusTag,
  calcOtherSetting,
  rankCorrectionEnum,
  rankCorrection,
  individualValueEnum,
  individualValue,
} from "../types/pokemon";

export { rankCorrectionEnumList, individualValueEnumList } from "../types/pokemon";
export { toOffence, toDefence } from "../lib/pokemon/convert";
export { hasPokemonStatus, setPokemonStatus } from "../lib/pokemon/status";

const enrichPokemon = (pokemon: PokemonV2): PokemonV2 => ({
  ...pokemon,
  move_list: getPokemonMovesByUsage(pokemon),
  ability_list: getAbilitiesByUsage(pokemon),
});

// 使用率の昇順で並べ、同率は species_id → id で安定化する
const sortByUsageRate = (a: PokemonV2, b: PokemonV2) => {
  if (a.usage_rate !== b.usage_rate) {
    return a.usage_rate - b.usage_rate;
  }
  if (a.species_id !== b.species_id) {
    return a.species_id - b.species_id;
  }
  return a.id - b.id;
};

// マスターデータの読み込み後にまとめて更新する
let cachedMasterListRef: PokemonV2[] | null = null;
export let pokemon_list: PokemonV2[] = [];
export let all_pokemon_list_for_show: PokemonV2[] = [];

const refreshPokemonList = (basePokemonData: PokemonV2[]) => {
  const enrichedPokemonList = basePokemonData.map(enrichPokemon);
  const orderedPokemonList = [...enrichedPokemonList].sort(sortByUsageRate);
  all_pokemon_list_for_show = orderedPokemonList;
  // usage_rate=999は候補外扱いなので通常の一覧から除外する
  pokemon_list = orderedPokemonList.filter((p) => p.usage_rate < 999);
};

// 参照元が変わったときだけ再構築し、同期呼び出しでも最新リストを返す
const ensurePokemonList = () => {
  const basePokemonData = getPokemonMasterList();
  if (basePokemonData.length === 0) {
    if (cachedMasterListRef !== basePokemonData) {
      cachedMasterListRef = basePokemonData;
      all_pokemon_list_for_show = [];
      pokemon_list = [];
    }
    return all_pokemon_list_for_show;
  }
  if (cachedMasterListRef !== basePokemonData) {
    cachedMasterListRef = basePokemonData;
    refreshPokemonList(basePokemonData);
  }
  return all_pokemon_list_for_show;
};

export const loadPokemonList = async (): Promise<PokemonV2[]> => {
  const basePokemonData = await loadPokemonMasterList();
  // 非同期読み込み完了後にキャッシュと一覧を更新する
  if (cachedMasterListRef !== basePokemonData) {
    cachedMasterListRef = basePokemonData;
    refreshPokemonList(basePokemonData);
  }
  return all_pokemon_list_for_show;
};

// TOP150の使用率ポケモンを取得する関数
export const getTop150PokemonList = () => ensurePokemonList().slice(0, 150);

// 使用率150位圏外も含む全ポケモンを取得する関数
export const getAllPokemonList = () => ensurePokemonList();

// なんかいろんなところで使う予定
export function dummyPokemon(): PokemonV2 {
  return {
    id: 9999999,
    name_ja: "ああああ",
    hp: 1,
    attack: 1,
    defense: 1,
    special_attack: 1,
    special_defense: 1,
    speed: 1,
    picture_url: "",
    species_id: 1,
    type_id_list: [],
    ability_id_list: [],
    move_id_list: [],
    weight: 1,
    often_used_move: [],
    often_used_tokusei: [],
    often_used_terasu: [],
    usage_rate: 999,
    other_setting: {},
    effective_slider_step: {
      hp: 0,
      attack: 0,
      defense: 0,
      special_attack: 0,
      special_defense: 0,
      speed: 0,
    },
    effective_value: {
      hp: 0,
      attack: 0,
      defense: 0,
      special_attack: 0,
      special_defense: 0,
      speed: 0,
    },
    rankCorrection: {
      attack: 0,
      defense: 0,
      special_attack: 0,
      special_defense: 0,
      speed: 0,
    },
    personality: {
      attack: 1.0,
      defense: 1.0,
      special_attack: 1.0,
      special_defense: 1.0,
      speed: 1.0,
    },
    individualValue: {
      hp: 31,
      attack: 31,
      defense: 31,
      special_attack: 31,
      special_defense: 31,
      speed: 31,
    },
  };
}
